/**
 * Client-side export helpers for proctoring admin audit (violations + transcripts).
 */

import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

function escapeCsvCell(val) {
  const s = val == null ? '' : String(val);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function violationsToRows(violations, studentNameById) {
  return (violations || []).map((v) => {
    const uid = String(v.userId ?? v.user_id ?? '');
    const iacRaw = v.iacId != null ? v.iacId : v.iac_id;
    return {
      timestamp: v.timestamp != null ? String(v.timestamp) : '',
      studentId: uid,
      studentName: studentNameById[uid] || '',
      type: v.type != null ? String(v.type) : '',
      severity: v.severity != null ? String(v.severity) : '',
      details: v.details != null ? String(v.details) : '',
      snapshotUrl: v.snapshot_url != null ? String(v.snapshot_url) : '',
      iacId: iacRaw != null && iacRaw !== '' ? String(iacRaw) : ''
    };
  });
}

function transcriptsToRows(studentTranscripts, students) {
  const nameById = {};
  (students || []).forEach((s) => {
    const id = String(s.user_id ?? s.id ?? s.learner_id ?? '');
    if (id) nameById[id] = `${s.first_name || ''} ${s.last_name || ''}`.trim();
  });
  const rows = [];
  Object.entries(studentTranscripts || {}).forEach(([userId, entries]) => {
    if (!Array.isArray(entries)) return;
    entries.forEach((e) => {
      rows.push({
        studentId: String(userId),
        studentName: nameById[String(userId)] || '',
        transcriptTime: e.timestamp != null ? String(e.timestamp) : '',
        transcript: e.transcript != null ? String(e.transcript) : ''
      });
    });
  });
  return rows;
}

export function buildAuditExportPayload(violations, students, studentTranscripts) {
  const studentNameById = {};
  (students || []).forEach((s) => {
    const id = String(s.user_id ?? s.id ?? s.learner_id ?? '');
    if (id) studentNameById[id] = `${s.first_name || ''} ${s.last_name || ''}`.trim();
  });
  return {
    violationRows: violationsToRows(violations, studentNameById),
    transcriptRows: transcriptsToRows(studentTranscripts, students)
  };
}

export function downloadAuditCsv(payload, filenameBase = 'proctoring-audit') {
  const v = payload.violationRows;
  const headers = ['timestamp', 'studentId', 'studentName', 'type', 'severity', 'details', 'snapshotUrl', 'iacId'];
  const lines = [headers.join(',')];
  v.forEach((row) => {
    lines.push(headers.map((h) => escapeCsvCell(row[h])).join(','));
  });
  if (payload.transcriptRows.length) {
    lines.push('');
    lines.push('--- Transcripts ---');
    const th = ['studentId', 'studentName', 'transcriptTime', 'transcript'];
    lines.push(th.join(','));
    payload.transcriptRows.forEach((row) => {
      lines.push(th.map((h) => escapeCsvCell(row[h])).join(','));
    });
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${filenameBase}.csv`);
}

export function downloadAuditXlsx(payload, filenameBase = 'proctoring-audit') {
  const wb = XLSX.utils.book_new();
  const wsV = XLSX.utils.json_to_sheet(payload.violationRows);
  XLSX.utils.book_append_sheet(wb, wsV, 'Violations');
  if (payload.transcriptRows.length) {
    const wsT = XLSX.utils.json_to_sheet(payload.transcriptRows);
    XLSX.utils.book_append_sheet(wb, wsT, 'Transcripts');
  }
  XLSX.writeFile(wb, `${filenameBase}.xlsx`);
}

export function downloadAuditPdf(payload, title, filenameBase = 'proctoring-audit') {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  doc.setFontSize(14);
  doc.text(title, 14, 16);
  const v = payload.violationRows;
  const vBody = v.map((r) => [
    r.timestamp,
    r.studentId,
    r.studentName,
    r.type,
    r.severity,
    r.details.length > 80 ? `${r.details.slice(0, 77)}...` : r.details,
    r.snapshotUrl ? 'yes' : '',
    r.iacId || '—'
  ]);
  autoTable(doc, {
    startY: 22,
    head: [['Time', 'Student ID', 'Name', 'Type', 'Severity', 'Details', 'Snap', 'IAC']],
    body: vBody.length ? vBody : [['—', '—', '—', 'No violations in session', '', '', '', '']],
    styles: { fontSize: 7 },
    headStyles: { fillColor: [66, 66, 66] }
  });
  let nextY = (doc.lastAutoTable && doc.lastAutoTable.finalY) ? doc.lastAutoTable.finalY + 10 : 40;
  if (payload.transcriptRows.length) {
    doc.setFontSize(11);
    if (nextY > 180) {
      doc.addPage();
      nextY = 20;
    }
    doc.text('Transcripts', 14, nextY);
    nextY += 6;
    const tBody = payload.transcriptRows.map((r) => [
      r.studentId,
      r.studentName,
      r.transcriptTime,
      r.transcript.length > 120 ? `${r.transcript.slice(0, 117)}...` : r.transcript
    ]);
    autoTable(doc, {
      startY: nextY,
      head: [['Student ID', 'Name', 'Time', 'Transcript']],
      body: tBody,
      styles: { fontSize: 7 },
      headStyles: { fillColor: [66, 66, 66] }
    });
  }
  doc.save(`${filenameBase}.pdf`);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
